/* eslint-disable react-hooks/exhaustive-deps */
import { memo, useCallback } from "react"
import type { FC, NamedExoticComponent, ReactElement } from "react"

import isString from "lodash-es/isString"

import { BetaMark, ETypographySize, ETypographyTag, InfoBox, Typography } from "@/3514/components"
import { EPath } from "@/3514/constants"
import { EFeedbackModalType, EFeedbackModalVariant } from "@/3514/modals"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { type TEmptyCallback } from "@/shared/types/functions"

import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatContext as useChatContext } from "../context"
import { coachingChatComponentsGuards as guards } from "../guards"
import { coachingChatStyles as chatStyles } from "../styles"
import { ECoachingChatSummaryCardMode as ESummaryCardsMode } from "../types"
import { coachingChatApiUtils as apiUtils } from "../utils"

import { CoachingChatSummaryCards as SummaryCards } from "./coaching-chat.summary-cards.component"

const { withModeInsights: withGuard } = guards

const { getSummaryCardModelsFromDtos: getSummaryCards } = apiUtils

const { CHAT_AUTOSCROLL_SEARCH_PARAM_NAME: PARAM_NAME, CHAT_AUTOSCROLL_SEARCH_PARAM_VALUE: PARAM_VALUE } = uiConfig

const { insights: classes } = chatStyles

const Insights: FC = (): ReactElement => {
    const { t }: IUseTranslation = useTranslation()

    const {
        insights: { isEnabled, previousChatCompletionDate, previousChapterId, summaryCards },
        feedback: { handleModalOpen }
    }: ReturnType<typeof useChatContext> = useChatContext()

    const handleBetaMarkAction: TEmptyCallback = useCallback(
        (): void =>
            handleModalOpen({
                variant: EFeedbackModalVariant.Insights,
                type: EFeedbackModalType.General,
                payload: { chapterId: previousChapterId }
            }),
        [previousChapterId]
    )

    return isEnabled ? (
        <div className={classes.container}>
            <div className={classes.titleWrapper}>
                <Typography
                    className={classes.title}
                    tag={ETypographyTag.Heading3}
                    size={ETypographySize.Heading2}
                    text={t("entities.coachingChat.insights.title")}
                />
                <BetaMark handleAction={handleBetaMarkAction} />
            </div>
            {isString(previousChatCompletionDate) ? (
                <SummaryCards
                    cards={getSummaryCards(summaryCards, previousChatCompletionDate)}
                    mode={ESummaryCardsMode.BuildInInsights}
                />
            ) : (
                <InfoBox
                    variant="warning"
                    title={t("entities.coachingChat.insights.incomplete.title")}
                    message={t("entities.coachingChat.insights.incomplete.hint")}
                    action={{
                        to: EPath.Participant_ChapterId(previousChapterId, `${PARAM_NAME}=${PARAM_VALUE}`),
                        children: t("entities.coachingChat.insights.incomplete.hintAction")
                    }}
                />
            )}
        </div>
    ) : null
}

const MemoizedInsights: NamedExoticComponent = memo(Insights)

const GuardedInsights: FC = withGuard(MemoizedInsights)

GuardedInsights.displayName = "CoachingChatInsights"

export { GuardedInsights as CoachingChatInsights }
